/**
 * firestoreSync.js
 *
 * Syncs user learning statistics between localStorage and Firestore so that
 * stats persist across devices, browsers, and incognito sessions.
 *
 * Strategy:
 *  - On sign-in: merge Firestore data INTO localStorage (restore cloud data)
 *  - On stats change: debounced write FROM localStorage TO Firestore
 *  - On page unload: immediate flush to Firestore
 *
 * All merges are additive — we always take the union / maximum so that
 * data from different devices is combined rather than overwritten.
 */

import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

// ─── Merge helpers ────────────────────────────────────────────────────────────

function mergeReadStories(local, remote) {
  return [...new Set([...local, ...remote])];
}

function mergeNumericMap(a, b) {
  const result = { ...a };
  for (const [k, v] of Object.entries(b)) {
    result[k] = Math.max(result[k] || 0, typeof v === 'number' ? v : 0);
  }
  return result;
}

function mergeHistory(a, b) {
  const byDay = { ...(a.byDay || {}) };
  for (const [day, val] of Object.entries(b.byDay || {})) {
    if (!byDay[day]) {
      byDay[day] = val;
    } else {
      byDay[day] = {
        words: Math.max(byDay[day].words || 0, val.words || 0),
        seconds: Math.max(byDay[day].seconds || 0, val.seconds || 0),
      };
    }
  }
  return { byDay };
}

function mergeUserStats(local, remote) {
  return {
    wordsRead: Math.max(local.wordsRead || 0, remote.wordsRead || 0),
    listeningSeconds: Math.max(local.listeningSeconds || 0, remote.listeningSeconds || 0),
    listeningSecondsByStory: mergeNumericMap(
      local.listeningSecondsByStory || {},
      remote.listeningSecondsByStory || {}
    ),
    history: mergeHistory(local.history || {}, remote.history || {}),
  };
}

function mergeLearnedChars(local, remote) {
  const result = { ...remote };
  for (const [storyId, chars] of Object.entries(local)) {
    if (!result[storyId]) {
      result[storyId] = chars;
    } else {
      result[storyId] = [...new Set([...result[storyId], ...(Array.isArray(chars) ? chars : [])])];
    }
  }
  return result;
}

// ─── Load: Firestore → localStorage ──────────────────────────────────────────

/**
 * Called on sign-in. Accepts the userData already fetched from Firestore by
 * App.js so no extra network round-trip is needed.
 *
 * Merges cloud data into localStorage and dispatches events so all components
 * re-render with the restored stats.
 */
export function mergeFirestoreStatsToLocal(uid, userData) {
  if (!uid || !userData) return;

  let changed = false;

  try {
    // 1. readStories (union)
    if (Array.isArray(userData.readStories) && userData.readStories.length > 0) {
      const raw = localStorage.getItem(`readStories:${uid}`);
      const local = raw ? (JSON.parse(raw) || []) : [];
      const merged = mergeReadStories(local, userData.readStories);
      if (merged.length !== local.length) {
        localStorage.setItem(`readStories:${uid}`, JSON.stringify(merged));
        changed = true;
      }
    }

    // 2. userStats (take maximums)
    if (userData.userStats && typeof userData.userStats === 'object') {
      const raw = localStorage.getItem(`dumplingo_user_stats:${uid}`);
      const local = raw ? (JSON.parse(raw) || {}) : {};
      const merged = mergeUserStats(local, userData.userStats);
      localStorage.setItem(`dumplingo_user_stats:${uid}`, JSON.stringify(merged));
      changed = true;
    }

    // 3. learnedCharactersByStory (union per story)
    if (userData.learnedCharactersByStory && typeof userData.learnedCharactersByStory === 'object') {
      const raw = localStorage.getItem(`learnedCharactersByStory:${uid}`);
      const local = raw ? (JSON.parse(raw) || {}) : {};
      const merged = mergeLearnedChars(local, userData.learnedCharactersByStory);
      localStorage.setItem(`learnedCharactersByStory:${uid}`, JSON.stringify(merged));
      changed = true;
    }
  } catch (e) {
    console.warn('[firestoreSync] merge failed:', e);
  }

  // Fire update events so all components re-render with restored data
  if (changed) {
    // Read merged stats back from localStorage so subscribers get the real values
    let mergedStats;
    try {
      const raw = localStorage.getItem(`dumplingo_user_stats:${uid}`);
      mergedStats = raw ? JSON.parse(raw) : null;
    } catch {}

    if (mergedStats) {
      window.dispatchEvent(new CustomEvent('user-stats-updated', {
        detail: { userKey: `dumplingo_user_stats:${uid}`, stats: mergedStats }
      }));
    }
    window.dispatchEvent(new CustomEvent('learned-characters-updated'));
    window.dispatchEvent(new CustomEvent('story-read-updated'));
  }
}

// ─── Save: localStorage → Firestore ──────────────────────────────────────────

let _saveTimer = null;

/**
 * Schedule a debounced save (3 s) to Firestore.
 * Safe to call on every stat change — only one write will happen per quiet period.
 */
export function scheduleSaveUserStats(uid) {
  if (!uid || uid === 'guest') return;
  clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => _doSave(uid), 3_000);
}

/**
 * Cancel any pending debounced save (e.g. before a course reset so the old
 * completed-stories list is not merged back from Firestore).
 */
export function cancelPendingSave() {
  clearTimeout(_saveTimer);
  _saveTimer = null;
}

/**
 * Overwrite the readStories field in Firestore with a specific list, bypassing
 * the usual additive merge. Use this after a course reset so that Firestore no
 * longer holds the old completed-stories and cannot restore them on the next sync.
 */
export async function forceWriteReadStories(uid, readStories) {
  if (!uid || uid === 'guest') return;
  try {
    const db = getFirestore();
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, { readStories }, { merge: true });
  } catch (e) {
    console.warn('[firestoreSync] forceWriteReadStories failed:', e);
  }
}

/**
 * Immediately flush pending save to Firestore (e.g. on beforeunload).
 * Returns a Promise so the caller can await it if needed.
 */
export function flushUserStats(uid) {
  if (!uid || uid === 'guest') return Promise.resolve();
  clearTimeout(_saveTimer);
  return _doSave(uid);
}

async function _doSave(uid) {
  try {
    const db = getFirestore();
    const userRef = doc(db, 'users', uid);

    // ── Read local data ───────────────────────────────────────────────────────
    let localReadStories = [];
    let localUserStats = null;
    let localLearnedChars = null;
    try { localReadStories = JSON.parse(localStorage.getItem(`readStories:${uid}`) || '[]'); } catch {}
    try { localUserStats = JSON.parse(localStorage.getItem(`dumplingo_user_stats:${uid}`) || 'null'); } catch {}
    try { localLearnedChars = JSON.parse(localStorage.getItem(`learnedCharactersByStory:${uid}`) || 'null'); } catch {}

    const hasLocal =
      localReadStories.length > 0 ||
      (localUserStats && (localUserStats.wordsRead > 0 || localUserStats.listeningSeconds > 0)) ||
      (localLearnedChars && Object.keys(localLearnedChars).length > 0);

    if (!hasLocal) return; // nothing meaningful to save

    // ── Read current Firestore state ──────────────────────────────────────────
    // IMPORTANT: setDoc replaces array/object fields entirely; we must merge
    // manually so that saves from different devices never clobber each other.
    const snap = await getDoc(userRef);
    const remote = snap.exists() ? snap.data() : {};

    // ── Merge ─────────────────────────────────────────────────────────────────
    const mergedReadStories = mergeReadStories(localReadStories, remote.readStories || []);
    const mergedStats      = mergeUserStats(localUserStats || {}, remote.userStats || {});
    const mergedLearnedChars = mergeLearnedChars(localLearnedChars || {}, remote.learnedCharactersByStory || {});

    const payload = {};
    if (mergedReadStories.length > 0) payload.readStories = mergedReadStories;
    if (mergedStats.wordsRead > 0 || mergedStats.listeningSeconds > 0) payload.userStats = mergedStats;
    if (Object.keys(mergedLearnedChars).length > 0) payload.learnedCharactersByStory = mergedLearnedChars;

    if (Object.keys(payload).length === 0) return;

    await setDoc(userRef, payload, { merge: true });

    // ── Write merged stats back to localStorage so local reflects full truth ──
    // NOTE: readStories is intentionally NOT written back here. Writing the
    // Firestore-merged set back to localStorage can restore unmarked stories
    // (Firestore still holds the old mark) and can also lose marks made during
    // the async Firestore round-trip (race condition). Cross-device restore for
    // readStories already happens at sign-in via mergeFirestoreStatsToLocal.
    if (payload.userStats) {
      localStorage.setItem(`dumplingo_user_stats:${uid}`, JSON.stringify(mergedStats));
    }
    if (payload.learnedCharactersByStory) {
      localStorage.setItem(`learnedCharactersByStory:${uid}`, JSON.stringify(mergedLearnedChars));
    }
  } catch (e) {
    console.warn('[firestoreSync] save failed:', e);
  }
}
